/** Welcome onboarding steps shell: progress bar + step routing. */
import { LinearProgress, Stack } from '@mui/material';
import { useT } from '@/i18n/useT';
import { useOnboardingWelcomeSteps } from '@/features/onboarding/hooks/useOnboardingWelcomeSteps';
import OnboardingWelcomeDisplayNameStep from '@/features/onboarding/components/OnboardingWelcomeDisplayNameStep';
import OnboardingWelcomeTaskStep from '@/features/onboarding/components/OnboardingWelcomeTaskStep';
import OnboardingWelcomeProjectStep from '@/features/onboarding/components/OnboardingWelcomeProjectStep';
import OnboardingWelcomeSubtaskStep from '@/features/onboarding/components/OnboardingWelcomeSubtaskStep';

const OnboardingWelcomeSteps = () => {
  const t = useT();
  const {
    step,
    stepNumber,
    stepTotal,
    progressPercent,
    projectInputRef,
    projectInput,
    setProjectInput,
    parsedProject,
    projectDone,
    handleTaskAdded,
    handleProjectSubmit,
    parentTitle,
    parentTask,
    addedSubtask,
    subtaskEditing,
    subtaskSaving,
    subtaskInput,
    setSubtaskInput,
    startSubtaskEdit,
    handleSubtaskEnter,
    handleSubtaskPrimary,
    displayNameInitialValue,
    displayNameSaving,
    displayNameErrorKey,
    handleDisplayNameSubmit,
  } = useOnboardingWelcomeSteps();

  // Name step keeps a 0% bar for layout height; omit "Stap X van Y" until content steps.
  const progressLabel =
    stepNumber > 0
      ? t('onboarding.tour.progress', 'Stap {current} van {total}', {
          current: stepNumber,
          total: stepTotal,
        })
      : undefined;

  return (
    <Stack
      spacing={2}
      sx={{ width: '100%' }}
      data-testid="onboarding-welcome-steps"
    >
      <LinearProgress
        variant="determinate"
        value={progressPercent}
        aria-label={progressLabel}
        aria-valuetext={progressLabel}
        sx={{ height: 6, borderRadius: 1 }}
      />

      {step === 'displayName' ? (
        <OnboardingWelcomeDisplayNameStep
          initialValue={displayNameInitialValue}
          saving={displayNameSaving}
          errorKey={displayNameErrorKey}
          onSubmit={handleDisplayNameSubmit}
        />
      ) : null}

      {step === 'task' ? (
        <OnboardingWelcomeTaskStep onTaskAdded={handleTaskAdded} />
      ) : null}

      {step === 'project' ? (
        <OnboardingWelcomeProjectStep
          projectInputRef={projectInputRef}
          projectInput={projectInput}
          setProjectInput={setProjectInput}
          parsedProject={parsedProject}
          projectDone={projectDone}
          onProjectSubmit={handleProjectSubmit}
        />
      ) : null}

      {step === 'subtask' ? (
        <OnboardingWelcomeSubtaskStep
          parentTitle={parentTitle}
          parentTask={parentTask}
          addedSubtask={addedSubtask}
          subtaskEditing={subtaskEditing}
          subtaskSaving={subtaskSaving}
          subtaskInput={subtaskInput}
          setSubtaskInput={setSubtaskInput}
          onStartSubtaskEdit={startSubtaskEdit}
          onSubtaskEnter={handleSubtaskEnter}
          onSubtaskPrimary={handleSubtaskPrimary}
        />
      ) : null}
    </Stack>
  );
};

export default OnboardingWelcomeSteps;
